import {
    LegacyCard,
    DescriptionList,
    Layout,
    ResourceList,
    ResourceItem,
    Frame,
    Page,
    Pagination,
    EmptyState,
    Badge,
    Button,
    LegacyStack,
    Spinner,
    TextField,
    Icon,
    Form,
    ButtonGroup,
    Modal,
    Banner,
    Text,
} from "@shopify/polaris";
import {useCallback, useEffect} from 'react';
import { useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Api from '../../helpers/api';
import CustomerOnHold from "./CustomerOnHold";
import {str_slug, getFormError} from "../../helpers/utils";
import {SearchMajor} from "@shopify/polaris-icons";

const Paginator = ({ collection = [], meta, callback, customerId}) => {
    if (! collection.length) {
        return null;
    }

    return (
        <LegacyCard.Section>
            <Pagination
                label={`Showing Page ${meta.current_page} of ${meta.last_page}`}
                hasPrevious={ meta.current_page > 1}
                onPrevious={() => { callback(customerId, meta.current_page - 1) }}
                hasNext={ meta.current_page < meta.last_page }
                onNext={() => { callback(meta.current_page + 1) }} />
        </LegacyCard.Section>
    );
}

const LocationsPaginator = ({ locations = [], locationsMeta, getLocations }) => {
    if (! locations.length) {
        return null;
    }

    return (
        <LegacyCard.Section>
            <Pagination
                label={`Showing Page ${locationsMeta.current_page} of ${locationsMeta.last_page}`}
                hasPrevious={ locationsMeta.current_page > 1}
                onPrevious={() => { getLocations(locationsMeta.current_page - 1) }}
                hasNext={ locationsMeta.current_page < locationsMeta.last_page }
                onNext={() => { getLocations(locationsMeta.current_page + 1) }} />
        </LegacyCard.Section>
    );
}

const AddressBlock = ({ location, label }) => location
    ? (
        <div>
            <h3><Text  variant="bodyMd" as="span" fontWeight="semibold">{ label }</Text></h3>
            {location.company && <>{location.company}<br /></>}
            {location.address1}<br />
            {location.address2 && <>{location.address2}<br /></>}
            {location.city}, {location.state} {location.zip_code}
            <p>
                { location.type && <div><Text  variant="bodyMd" as="span" fontWeight="semibold">Type:</Text> {location.type || 'Not Defined'}</div>}
            </p>
        </div>
    )
    : null;

const LocationsEmptyState = ({ locations }) => {
    if (locations.length) {
        return null;
    }

    return (<EmptyState heading="The customer has no additional locations"></EmptyState>)
}

const OnHoldToggle = ({ customer, setCustomer }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({can_manage_on_hold: false, is_admin: false})
    const [hasOrders, setHasOrders] = useState(true);
    const history = useHistory();

    useEffect(() => {
        setLoading(true);

        Promise
            .all([

                Api.hasCustomerOrders(customer.id),
                Api.getMe()
            ])
            .then(results => {
                setHasOrders(results[0].data.status ?? false)
                setUser(results[1].data.user ?? {can_manage_on_hold: false, is_admin: false})
            })
            .finally(() => setLoading(false));
    }, [customer.id]);

    const toggleOnHold = useCallback(async () => {
        if (loading)
            return;

        setLoading(true);
        const response = await Api.toggleCustomerOnHold(customer.id);
        setCustomer(response.data.data);
        setLoading(false);
    }, [customer, loading]);

    const handleDelete = useCallback(() => {
        if (loading) {
            return;
        }

        setLoading(true);
        const confirmation = confirm("Are you sure you want to delete this customer?");

        if (confirmation) {
            Api.deleteCustomer(customer.id).then(() => {
                history.goBack();
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [loading, customer]);

    return (
        <LegacyCard sectioned>
            <ButtonGroup>
                <Button variant="primary" tone="critical" onClick={toggleOnHold} loading={loading} disabled={loading || (!user.can_manage_on_hold && !user.is_admin)}>
                    {customer?.is_on_hold ? 'Unset from hold' : 'Set on hold'}
                </Button>
                <Button variant="primary" onClick={() => {history.push(`/customers/${customer.id}/refunds`); }}>
                    Refunds
                </Button>
            </ButtonGroup>
        </LegacyCard>
    );
}

const LocationsCard = ({ customer, placeOrder }) => {
    if (! customer.id) {
        return null;
    }

    const history = useHistory();
    const [locations, setLocations] = useState([]);
    const [locationsMeta, setLocationsMeta] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const getLocations = useCallback((page = 1, query = null) => {
        let searchParam = query ?? search;

        if (searchParam && searchParam.length > 0 && searchParam.length < 3) {
            alert("You need to type at least 3 characters");
            return;
        }

        setLoading(true);
        Api
            .getLocations(customer.id, page, searchParam)
            .then(({ data }) => {
                setLocations(data.data);
                setLocationsMeta(data.meta);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [locations, locationsMeta, loading, search, customer.id])

    useEffect(() => {
        getLocations(1);
    }, [customer.id]);

    return (
        <LegacyCard
            title="Locations"
            sectioned
            actions={[
                { content: 'Add location', onAction: () => history.push(`/customers/${customer.id}/locations/create`) },
            ]}>
            <LegacyCard.Section>
                <Form
                    onSubmit={() => {
                        getLocations(1);
                    }}
                >
                    <TextField
                        placeholder={"Search locations"}
                        value={search}
                        onChange={setSearch}
                        clearButton
                        onClearButtonClick={() => {
                            setSearch('');
                            getLocations(1, '');
                        }}
                        connectedRight={(
                            <Button
                                primary
                                onClick={() => {
                                    getLocations(1)
                                }}
                            >
                                <Icon source={SearchMajor} />
                            </Button>
                        )}
                    />
                </Form>
            </LegacyCard.Section>
            <LocationsPaginator locations={locations}
                                locationsMeta={locationsMeta}
                                getLocations={getLocations} />
            <LegacyCard.Section>
                { loading && (
                    <Spinner size={"small"} />
                )}
                { !loading && (
                    <ResourceList
                        resourceName={{singular: 'Location', plural: 'Locations'}}
                        items={locations}
                        emptyState={<LocationsEmptyState locations={locations} />}
                        renderItem={(location) => {
                            const shortcutActions = [
                                {
                                    content: 'Place Order',
                                    accessibilityLabel: `Place order for location ${location.id}`,
                                    onAction: () => { placeOrder(location.id); }
                                }
                            ]

                            const addresses = [
                                {location: location.billing, label: 'Billing Address'},
                                {location: location.shipping, label: 'Shipping Address'},
                            ].filter(item => item.location);

                            const badges = [
                                location.default && <Badge size='small'>Default</Badge>,
                                location.shopify_customer_id && <Badge size='small'>Shopify Customer</Badge>,
                            ].filter(Boolean);

                            return (
                                <ResourceItem onClick={ () => { history.push(`/locations/${location.id}`); }} shortcutActions={shortcutActions}>
                                    <LegacyStack vertical>
                                        <div>{badges}</div>
                                        { location.phone && <div><Text  variant="bodyMd" as="span" fontWeight="semibold">Phone:</Text> {location.phone}</div> }
                                        <LegacyStack>
                                            {addresses.map((item, i) => (<AddressBlock {...item} key={`location-${i}`} />))}
                                        </LegacyStack>
                                        <div>
                                            <h3><Text  variant="bodyMd" as="span" fontWeight="semibold">Notes</Text></h3>
                                            {location.notes}
                                        </div>
                                    </LegacyStack>
                                </ResourceItem>
                            );
                        }}
                    />
                )}
            </LegacyCard.Section>
            <LocationsPaginator locations={locations}
                                locationsMeta={locationsMeta}
                                getLocations={getLocations} />
        </LegacyCard>
    );
}

const ShowCustomer = () => {
    const history = useHistory();
    const [customer, setCustomer] = useState(null);
    const {customerId} = useParams();

    const placeOrder = (location_id) => {
        history.push(`/orders/${customer.id}?location_id=${location_id}`);
    }

    const placeRefund = () => {
        history.push(`/createRefund/${customer.id}`);
    }

    useEffect(() => {
        getCustomer(customerId);
    }, [customerId]);

    const getCustomer = (customerId) => {
        Api.getCustomer(customerId).then( response => {
            setCustomer(response.data.data);
        });
    }

    const editCustomer = () => {
        history.push(`/customers/${customerId}/edit`);
    }

    const primaryFooterAction = customer?.default_location
        ? { content: 'Place Order', onAction: () => { placeOrder(customer.default_location.id) } }
        : null;

    const secondaryFooterActions = [
        { content: 'Add refund', onAction: () => { placeRefund() } }
    ];

    const primaryAction = {
        content: 'Edit Customer',
        onAction: editCustomer,
    };

    const getCustomerFinancialInformation = () => {
        const data = [
            { term: 'Total Unpaid', 'description': `$${(customer.orders_sum_total_outstanding ?? 0).toFixed(2)}` },
        ];


        return data;
    }

    return customer && (
        <Frame>
            <Page title={`Customer`}
                  primaryAction={primaryAction}
                  backAction={{ content: 'Go back', onAction: () => history.goBack() }}
            >
                <Layout>
                <CustomerOnHold customer={customer}/>
                <Layout.Section>
                    <LegacyCard
                        title="Account Information"
                        sectioned
                        primaryFooterAction={primaryFooterAction}
                        secondaryFooterActions={secondaryFooterActions}>
                        <DescriptionList items={[
                            { term: 'Shopify Customer ID', 'description': customer.shopify_customer_id },
                            { term: 'Raymark VIP Code', 'description': customer.raymark_vip_code },
                            { term: 'Raymark Other ID', 'description': customer.raymark_other_id },
                            { term: 'Advantage Customer ID', 'description': customer.advantage_customer_id },
                            { term: 'Email', 'description': customer.email },
                            { term: 'Email Marketing', 'description': customer.email_consent ? 'Yes' : 'No' },
                            { term: 'Phone', 'description': customer.phone_number },
                            { term: 'SMS Marketing', 'description': customer.sms_consent ? 'Yes' : 'No' },
                            { term: 'Name', 'description': `${customer.additional_data.first_name ?? ''} ${customer.additional_data.last_name ?? ''}` },
                        ]} />
                        <Text variant="headingMd" as="h2">Financial Information</Text>
                        <DescriptionList items={getCustomerFinancialInformation()} />
                    </LegacyCard>

                    <LocationsCard customer={customer} placeOrder={placeOrder} />
                </Layout.Section>
                <Layout.Section variant="oneThird">
                    <OnHoldToggle customer={customer} setCustomer={setCustomer} />
                    <LegacyCard sectioned title="Notes">
                        <div>{customer.additional_data.notes}</div>
                    </LegacyCard>
                </Layout.Section>
            </Layout>
            </Page>
        </Frame>
    );
}

export default ShowCustomer;
